import SourceProposalForm from './SourceProposalForm'
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { PlatformMfaRenewal } from '@/components/legalControl/PlatformMfaRenewal'
import * as api from '@/services/legalControl/sourceReview'

function Review({ account, session }: { account: string; session: string }) {
  const [initial] = useState(() => {
    try {
      return { intent: api.pending(account), error: '' }
    } catch {
      return {
        intent: null,
        error: 'Saved original intent unavailable. Recover storage before sending another command.',
      }
    }
  })
  const [reference, setReference] = useState(initial.intent?.review ?? ''),
    [view, setView] = useState<api.Receipt | null>(null)
  const [intent, setIntent] = useState(initial.intent),
    [error, setError] = useState(initial.error),
    [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false),
    [sha, setSha] = useState(''),
    [reason, setReason] = useState(''),
    [ack, setAck] = useState(false)
  const epoch = useRef(0)
  useEffect(
    () => () => {
      epoch.current++
    },
    []
  )
  const [clock, setClock] = useState(Date.now())
  const until = view ? Date.parse(view.command.expires_at) : 0
  useEffect(() => {
    if (until <= Date.now()) return
    const timer = setTimeout(
      () => setClock(Date.now()),
      Math.min(2147483647, until - Date.now() + 1)
    )
    return () => clearTimeout(timer)
  }, [until, clock])
  const expired = until <= Date.now()
  async function load() {
    if (busy) return
    const generation = ++epoch.current
    setBusy(true)
    setView(null)
    setAck(false)
    setError('')
    setMessage('')
    try {
      const result = await api.inspect(reference)
      let text = 'Original recorded review loaded.'
      if (intent && intent.review === result.command.review_id) {
        const recorded = intent.action === 'withdraw' ? result.revocation : result.decision
        if (recorded) {
          const matches =
            (await api.fingerprint(recorded.command)) === intent.fingerprint &&
            recorded.user_id === intent.user &&
            recorded.session_id === intent.session
          if (generation !== epoch.current) return
          api.forget(account)
          setIntent(null)
          text = matches
            ? 'Original command confirmed by its immutable receipt.'
            : 'A different immutable outcome is recorded. Your command is not confirmed; review the recorded outcome.'
        } else
          text =
            'Outcome still unresolved. Keep the original reference; do not send another command.'
      }
      if (generation !== epoch.current) return
      setView(result)
      setMessage(text)
    } catch {
      if (generation === epoch.current)
        setError(
          'Original receipt unavailable. Check access/MFA and retry the original lookup. Keep any saved intent.'
        )
    } finally {
      if (generation === epoch.current) setBusy(false)
    }
  }
  async function act(action: api.Action) {
    if (
      busy ||
      intent ||
      initial.error ||
      !view ||
      !ack ||
      view.revocation ||
      (action !== 'withdraw' &&
        (view.decision || expired || view.user_id === account || view.session_id === session))
    )
      return
    const generation = ++epoch.current
    setBusy(true)
    setError('')
    setMessage('')
    let saved = false
    try {
      const cmd = api.command(view.command.review_id, action, sha, reason)
      const original = {
        review: cmd.review_id,
        action,
        fingerprint: await api.fingerprint(cmd),
        user: account,
        session,
      }
      if (generation !== epoch.current) return
      api.remember(original)
      saved = true
      setIntent(original)
      setAck(false)
      await api.send(cmd, action)
      if (generation === epoch.current) {
        setView(null)
        setMessage('Command sent. Check the original outcome to confirm its recorded receipt.')
      }
    } catch (error) {
      if (generation === epoch.current) {
        if (saved) setView(null)
        if (saved && api.prewriteRefusal(error)) {
          try {
            api.forget(account)
            setIntent(null)
            setError(
              'Command refused before writing. Renew access/MFA and inspect the original review before trying again.'
            )
          } catch {
            setError(
              'Original intent storage unavailable. Keep the original reference for recovery.'
            )
          }
          return
        }
        setError(
          saved
            ? 'Outcome unknown. Check the original outcome; do not submit a replacement.'
            : 'Command not sent. Check evidence fields and original-intent storage.'
        )
      }
    } finally {
      if (generation === epoch.current) setBusy(false)
    }
  }
  const decisionDisabled =
    busy ||
    !!intent ||
    !!initial.error ||
    !view ||
    !!view.decision ||
    !!view.revocation ||
    expired ||
    view.user_id === account ||
    view.session_id === session ||
    !ack
  return (
    <main className="p-6 space-y-5">
      <h1 className="text-xl font-semibold">Source relationship reviews</h1>
      <p>
        Inspect a submitted relationship between HUF records. A review records an operator decision;
        it does not grant access, appoint a controller or link client identities.
      </p>
      <PlatformMfaRenewal />
      <SourceProposalForm
        account={account}
        session={session}
        blocked={!!intent || !!initial.error}
      />
      <section className="card p-5 space-y-3">
        <label>
          Original review reference
          <input
            className="input w-full"
            value={reference}
            disabled={busy || !!intent}
            onChange={e => {
              epoch.current++
              setReference(e.target.value)
              setView(null)
              setAck(false)
              setError('')
              setMessage('')
            }}
          />
        </label>
        <button className="btn" disabled={busy || !reference} onClick={() => void load()}>
          Check original outcome
        </button>
        {intent && (
          <p role="status">
            Saved {intent.action} command for {intent.review}. Recovery uses this original
            reference.
          </p>
        )}
        {error && <p role="alert">{error}</p>}
        {message && <p role="status">{message}</p>}
      </section>
      {view && (
        <section className="card p-5 space-y-3" aria-label="Recorded source review">
          <h2 className="font-semibold">Recorded source relationship</h2>
          <dl>
            <dt>Submitted by</dt>
            <dd>{view.user_id}</dd>
            <dt>Submitted at</dt>
            <dd>{view.submitted_at}</dd>
            <dt>Review</dt>
            <dd>{view.command.review_id}</dd>
            <dt>Original verification request</dt>
            <dd>{view.command.anchor_request_id}</dd>
            <dt>Source firm</dt>
            <dd>{String(view.command.target.tenant_id)}</dd>
            <dt>Source case</dt>
            <dd>{String(view.command.target.id)}</dd>
            <dt>Firm customer reference</dt>
            <dd>{String(view.command.target.customer_id)}</dd>
            <dt>Period</dt>
            <dd>
              {String(view.command.target.period_basis)} {String(view.command.target.period_value)}
            </dd>
            <dt>Source revision</dt>
            <dd>{String(view.command.target.current_revision)}</dd>
            <dt>Declared evidence reference</dt>
            <dd>{view.command.evidence_reference}</dd>
            <dt>Declared evidence fingerprint</dt>
            <dd>{view.command.evidence_sha256}</dd>
            <dt>Review expires</dt>
            <dd>{view.command.expires_at}</dd>
            <dt>Decision</dt>
            <dd>{view.decision?.command.decision ?? 'Not recorded'}</dd>
            <dt>Withdrawal</dt>
            <dd>{view.revocation ? 'Recorded' : 'Not recorded'}</dd>
          </dl>
          {view.decision && (
            <p>
              Decision by {view.decision.user_id} at {view.decision.decided_at}. Evidence
              fingerprint: {view.decision.command.evidence_sha256}
            </p>
          )}
          {expired && (
            <p>
              This review has expired. Its recorded history remains available; it can still be
              withdrawn.
            </p>
          )}
          {view.revocation && (
            <p>
              Withdrawn at {view.revocation.revoked_at}. Original decisions remain historical facts.
              Withdrawal evidence: {view.revocation.command.evidence_reference} (
              {view.revocation.command.evidence_sha256}).
            </p>
          )}
          {!view.revocation && (
            <>
              <label>
                Decision or withdrawal evidence fingerprint
                <input
                  className="input w-full"
                  value={sha}
                  maxLength={64}
                  disabled={busy || !!intent}
                  onChange={e => {
                    setSha(e.target.value)
                    setAck(false)
                  }}
                />
              </label>
              <label>
                Withdrawal evidence reference
                <input
                  className="input w-full"
                  value={reason}
                  maxLength={256}
                  disabled={busy || !!intent}
                  onChange={e => {
                    setReason(e.target.value)
                    setAck(false)
                  }}
                />
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={ack}
                  disabled={busy || !!intent}
                  onChange={e => setAck(e.target.checked)}
                />{' '}
                I reviewed this exact source and evidence. This action grants no authority.
              </label>
              <button
                className="btn"
                disabled={decisionDisabled}
                onClick={() => void act('approve')}
              >
                Approve relationship review
              </button>
              <button
                className="btn"
                disabled={decisionDisabled}
                onClick={() => void act('reject')}
              >
                Reject relationship review
              </button>
              <button
                className="btn"
                disabled={busy || !!intent || !!initial.error || !ack}
                onClick={() => void act('withdraw')}
              >
                Withdraw review
              </button>
            </>
          )}
        </section>
      )}
    </main>
  )
}
export function LegalSourceReviewPage() {
  const { user, hasPermission } = useAuth()
  const allowed =
    !!user &&
    user.role === 'cml_admin' &&
    user.authScope === 'platform' &&
    !user.tenantId &&
    !!user.currentSessionId &&
    hasPermission('txh.platform.legal_control.subject.review')
  if (!allowed || !user)
    return (
      <p role="alert">Use the original platform reviewer session with source review permission.</p>
    )
  return (
    <Review
      key={JSON.stringify([
        user.id,
        user.currentSessionId,
        user.role,
        user.authScope,
        user.tenantId,
        user.permissions,
      ])}
      account={user.id}
      session={user.currentSessionId!}
    />
  )
}
